// Turns the figures in the disclosure into an address a teacher can hand out.
//
// ⛔ No arithmetic and no validation of the plan. The server decides whether a
// curve is usable and discards it whole if it is not — duplicating that rule
// here would give two answers to one question, and the one the reader acts on
// is the server's. The inputs carry bounds from the coefficient file, so the
// browser nudges and the server decides.

const BUILD = '[data-ielts-curve-build]'
const INPUT = '[data-ielts-curve-input]'
const OUTPUT = '[data-ielts-curve-output]'
const COPIED = '[data-ielts-curve-copied]'

/**
 * `band:hours` pairs in band order.
 *
 * ⛔ Explicit keys, never positional values. A curve written by position would
 * shift the moment a band is inserted into the coefficient file, and the page
 * would then quote one band's cost under another band's name.
 *
 * @param {Array<HTMLInputElement>} inputs
 * @returns {string}
 */
export function curveParam (inputs) {
  // ⛔ An empty box is not a zero. `Number('')` is 0 and passes isFinite, so a
  // cleared field became `band:0` in the address and the server then discarded
  // the whole curve — the teacher's link silently fell back to our figures with
  // nothing on the page saying so.
  return inputs
    .filter((input) => input.value.trim() !== '')
    .map((input) => [Number(input.dataset.ieltsCurveInput), Number(input.value)])
    .filter(([band, hours]) => Number.isFinite(band) && Number.isFinite(hours))
    .sort((a, b) => a[0] - b[0])
    .map(([band, hours]) => `${band}:${hours}`)
    .join(',')
}

/**
 * ⛔ Built from the address the reader is on, so the combination they already
 * chose travels with the curve. A link that drops back to the empty form is a
 * link a student opens and has to fill in again.
 */
export function addressWithCurve (href, curve) {
  const url = new URL(href)
  url.searchParams.set('curve', curve)
  return url.toString()
}

export function setupCurveBuilder () {
  const build = document.querySelector(BUILD)
  const output = document.querySelector(OUTPUT)
  const copied = document.querySelector(COPIED)
  if (!build || !output) return

  build.addEventListener('click', () => {
    const address = addressWithCurve(
      window.location.href,
      curveParam(Array.from(document.querySelectorAll(INPUT)))
    )

    output.textContent = address
    output.hidden = false

    // Best effort. A reader whose browser refuses the clipboard can still select
    // the text, which is why the address is rendered and not only copied — and
    // why the confirmation only appears when the copy actually happened.
    navigator.clipboard?.writeText(address)
      .then(() => { if (copied) copied.hidden = false })
      .catch(() => {})
  })
}
